import './CodeWordNode.css';
import type {
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  Spread,
} from 'lexical';
import { $applyNodeReplacement, TextNode } from 'lexical';

export type SerializedCodeWordNode = Spread<
  {
    meaning: string;
  },
  SerializedTextNode
>;

export class CodeWordNode extends TextNode {
  meaning: string;

  static override getType(): string {
    return 'code-word';
  }

  static override clone(node: CodeWordNode): CodeWordNode {
    return new CodeWordNode(node.meaning, node.__text, node.__key);
  }

  constructor(meaning: string, text: string, key?: NodeKey) {
    super(text, key);
    this.meaning = meaning;
  }

  override createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.className = 'code-word';
    dom.setAttribute('spellcheck', 'false');

    dom.setAttribute('data-meaning', this.meaning);

    return dom;
  }

  override updateDOM(
    prevNode: this,
    dom: HTMLElement,
    config: EditorConfig,
  ): boolean {
    const inner = dom.firstChild;
    if (inner === null) {
      return true;
    }
    super.updateDOM(prevNode, inner as HTMLElement, config);
    return false;
  }

  static override importJSON(
    serializedNode: SerializedCodeWordNode,
  ): CodeWordNode {
    return $createCodeWordNode(
      serializedNode.meaning,
      serializedNode.text,
    ).updateFromJSON(serializedNode);
  }

  override exportJSON(): SerializedCodeWordNode {
    return {
      meaning: this.meaning,
      ...super.exportJSON(),
    };
  }

  override exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement('span');
    element.className = 'code-word';
    element.setAttribute('data-meaning', this.meaning);
    element.textContent = this.__text;
    return { element };
  }

  static override importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: Node) => {
        if (
          domNode instanceof HTMLElement &&
          domNode.classList.contains('code-word')
        ) {
          const meaning = domNode.getAttribute('data-meaning') || '';
          const text = domNode.textContent || '';
          return {
            conversion: convertCodeWordElement,
            priority: 1,
          };

          function convertCodeWordElement() {
            const node = $createCodeWordNode(meaning, text);
            return { node };
          }
        }
        return null;
      },
    };
  }

  override isTextEntity(): boolean {
    return true;
  }
}

export function $isCodeWordNode(
  node: LexicalNode | null | undefined,
): node is CodeWordNode {
  return node instanceof CodeWordNode;
}

export function $createCodeWordNode(
  meaning: string,
  emojiText: string,
): CodeWordNode {
  const node = new CodeWordNode(meaning, emojiText).setMode('segmented');
  return $applyNodeReplacement(node);
}
